import { useCallback, useEffect, useRef } from "react";
import { usePageVisibility } from "hooks/usePageVisibility";
import { useLazyGetAgentActionReviewQuery } from "pages/agent-action-review/agent-action-review-endpoints";

/**
 * How often an outstanding review is re-checked while the chat tab sits visible but unfocused.
 * Covers the case where the chat is parked on a second monitor and never clicked back into, so
 * neither `visibilitychange` nor `focus` ever fires.
 */
const POLL_INTERVAL_MS = 20000;

interface UseResumeOnReviewCompletionArgs {
  /** Reviews the agent asked to be told about, via AWAIT_AGENT_ACTION_REVIEW. */
  pendingReviewIds: string[];
  /** Called once per review, when it is no longer pending. */
  onCompleted: (reviewId: string) => void;
  isConnected: boolean;
}

/**
 * Tells the agent when a review the user went off to complete has settled.
 *
 * The user may submit the review anywhere — another tab, another window, another browser, even
 * another device — so rather than watching for any of those, this keys off the user's attention
 * returning to the chat, which is when the resume needs to have happened anyway. The service is
 * asked for the authoritative status; nothing is inferred from the review page.
 *
 * Deliberately never reconnects: if the socket is down the resume simply does not happen, and the
 * existing "Connection lost" flow stays the only recovery path. The pending id is kept, so a
 * user-initiated reconnect resumes through the ordinary triggers.
 */
export const useResumeOnReviewCompletion = ({
  pendingReviewIds,
  onCompleted,
  isConnected,
}: UseResumeOnReviewCompletionArgs): void => {
  const [fetchReview] = useLazyGetAgentActionReviewQuery();
  const isVisible = usePageVisibility();

  // Reviews already reported, so the overlapping triggers below resume each one exactly once.
  const reportedRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef<Set<string>>(new Set());

  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const idsKey = pendingReviewIds.join(",");

  const check = useCallback(async () => {
    for (const reviewId of pendingReviewIds) {
      if (
        reportedRef.current.has(reviewId) ||
        inFlightRef.current.has(reviewId)
      ) {
        continue;
      }
      inFlightRef.current.add(reviewId);
      try {
        const review = await fetchReview({ reviewId }).unwrap();
        if (review.status === "COMPLETED") {
          reportedRef.current.add(reviewId);
          onCompletedRef.current(reviewId);
        }
      } catch {
        // Transient failure: leave the review pending so the next trigger retries it.
      } finally {
        inFlightRef.current.delete(reviewId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, fetchReview]);

  // Mount, and whenever a review is added — also covers reconnecting, since isConnected is a dep.
  useEffect(() => {
    if (!isConnected || pendingReviewIds.length === 0) return;
    void check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check, idsKey, isConnected]);

  // The tab was hidden (another tab in front) and has come back.
  useEffect(() => {
    if (!isConnected || !isVisible || pendingReviewIds.length === 0) return;
    void check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, check, idsKey, isConnected]);

  // Separate windows side by side never hide the document, so only focus marks the return.
  useEffect(() => {
    if (!isConnected || pendingReviewIds.length === 0) return;
    const handleFocus = () => void check();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check, idsKey, isConnected]);

  // Visible but never refocused. Stops as soon as nothing is outstanding.
  useEffect(() => {
    if (!isConnected || !isVisible || pendingReviewIds.length === 0) return;
    const timer = setInterval(() => void check(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check, idsKey, isConnected, isVisible]);
};
